import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "layouts/Main";
import { NORD_POOL_TABS } from "helpers/tabs";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormControl } from "@mui/material";
import { ProtectedPaths } from "routes";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchBalancePlans } from "store/appSlice";
import { selectBalancePlans, selectMe } from "store/selectors";
import { BalancePlan } from "api/models/BalancePlan";
import { sendBalancePlan } from "api/resources/balancePlans";
import BalancePlansTable from "./BalancePlansTable";

interface Row {
  date: string;
  plans: BalancePlan[];
}

const BalancePlans = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [formattedDates, setFormattedDates] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [snackbar, setSnackbar] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const dispatch = useAppDispatch();
  const balancePlans = useAppSelector(selectBalancePlans);
  const me = useAppSelector(selectMe);
  const isAdmin =
    me?.roles?.find((role) => role.isCurrentRole)?.name === "ROLE_ADMIN";

  const reloadBalancePlans = () => {
    if (fromDate && toDate) {
      dispatch(
        fetchBalancePlans(
          dayjs(fromDate).format("YYYY-MM-DD"),
          dayjs(toDate).format("YYYY-MM-DD"),
        ),
      );
    }
  };

  const handleSend = async (plan: BalancePlan) => {
    try {
      const response = await sendBalancePlan(plan.id);
      setSnackbar({
        type: "success",
        message: `${plan.identification}: ${response?.data?.status ?? "sent"}`,
      });
    } catch (error) {
      console.error("Failed to send balance plan", error);
      setSnackbar({
        type: "error",
        message: `Failed to send ${plan.identification}`,
      });
    } finally {
      reloadBalancePlans();
    }
  };

  useEffect(() => {
    const fromDateParam = searchParams.get("from");
    const toDateParam = searchParams.get("to");

    if (fromDateParam) {
      setFromDate(dayjs(fromDateParam));
    } else {
      setFromDate(dayjs().subtract(7, "day"));
    }

    if (toDateParam) {
      setToDate(dayjs(toDateParam));
    } else {
      setToDate(dayjs().add(1, "day"));
    }
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      navigate(
        `/${ProtectedPaths.BalancePlans}?from=${dayjs(fromDate).format("YYYY-MM-DD")}&to=${dayjs(toDate).format("YYYY-MM-DD")}`,
        { replace: true },
      );
      dispatch(
        fetchBalancePlans(
          dayjs(fromDate).format("YYYY-MM-DD"),
          dayjs(toDate).format("YYYY-MM-DD"),
        ),
      );
    }
  }, [fromDate, toDate, dispatch]);

  useEffect(() => {
    const start = dayjs(fromDate).startOf("day");
    const end = dayjs(toDate).startOf("day");
    const dates = [];
    const totalDays = end.diff(start, "day");

    for (let i = 0; i <= totalDays; i++) {
      dates.push(start.add(i, "day").format("YYYY-MM-DD"));
    }

    setFormattedDates(dates);
  }, [fromDate, toDate]);

  const rows: Row[] = useMemo(() => {
    if (!formattedDates || !balancePlans) return [];

    const tempRows: Row[] = [];

    formattedDates.forEach((date) => {
      const matchingPlans = balancePlans.filter(
        (plan) => plan.targetDate === date,
      );
      tempRows.push({
        date,
        plans: matchingPlans,
      });
    });

    balancePlans.forEach((plan) => {
      if (!tempRows.find((row) => row.date === plan.targetDate)) {
        tempRows.push({
          date: plan.targetDate,
          plans: [plan],
        });
      }
    });

    return tempRows.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));
  }, [formattedDates, balancePlans]);

  return (
    <MainLayout
      tabs={NORD_POOL_TABS}
      snackbar={{
        open: snackbar !== null,
        handleClose: () => setSnackbar(null),
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      <Typography
        variant="h5"
        sx={{ position: "relative", maxWidth: "fit-content", mb: 2 }}
      >
        Balance plans
      </Typography>
      <Box sx={{ mb: 2 }}>
        <FormControl
          sx={{
            minWidth: 280,
            maxWidth: 280,
            background: "white",
            mr: 1,
          }}
          size="small"
        >
          <DatePicker
            slotProps={{ textField: { size: "small" } }}
            name="fromDate"
            value={fromDate}
            onChange={(value) => {
              setFromDate(dayjs(value));
            }}
            label="From date"
          />
        </FormControl>
        <FormControl
          sx={{
            minWidth: 280,
            maxWidth: 280,
            background: "white",
          }}
          size="small"
        >
          <DatePicker
            slotProps={{ textField: { size: "small" } }}
            name="toDate"
            value={toDate}
            onChange={(value) => {
              setToDate(dayjs(value));
            }}
            label="To date"
          />
        </FormControl>
      </Box>
      <Box sx={{ mt: "1rem" }}>
        {rows.map((row) => (
          <Accordion
            defaultExpanded={dayjs(row.date).isSame(
              dayjs().add(1, "day"),
              "day",
            )}
            sx={{ mb: "1rem", boxShadow: 2 }}
            key={"BalancePlanAccordion-" + row.date}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: "#eee" }}
            >
              <Typography
                component="h6"
                variant="h6"
                fontSize="1.1rem"
                fontWeight="400"
              >
                {`${dayjs(row.date).format("DD.MM.YYYY")} - ${dayjs(row.date).format("dddd")}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <BalancePlansTable
                plans={row.plans}
                canSend={isAdmin}
                onSend={handleSend}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </MainLayout>
  );
};

export default BalancePlans;
